/**
 * Helpers for the AI accessibility test rules (`EA_AI_R*`).
 *
 * AI rules arrive in the combined crawler report exactly like analytical rules,
 * distinguished only by their underscore id prefix. They have no explicit
 * "category" field, so the four test categories are derived from the rule-number
 * range. AI per-finding image assets live on a different CDN host than the report.
 */

import type { WcagLevel } from '@eye-able/types';

/**
 * The effective WCAG conformance level for a rule row.
 *
 * Analytical rules carry a resolved `wcagRule.wcagLvl`. AI rules frequently have
 * no `wcagRule` object (their WCAG chapter isn't in `wcagRules`), but their meta
 * declares an explicit `conformanceLevel`. Falling back to it keeps AI rows
 * visible under the critical-only filter (which filters on `A`/`AA`) and present
 * — with a level — in bookmarks and report exports. Mirrors the fallback the
 * page-detail table already uses.
 */
export function getEffectiveWcagLevel(
  ruleObj:
    | { wcagRule?: { wcagLvl?: WcagLevel | null } | null }
    | null
    | undefined,
  eaRule: { conformanceLevel?: WcagLevel | null } | null | undefined,
): WcagLevel | undefined {
  return ruleObj?.wcagRule?.wcagLvl ?? eaRule?.conformanceLevel ?? undefined;
}

/** Base URL where the AI test publishes per-finding image assets (context + original). */
export const AI_ASSETS_CDN_BASE = 'https://eye-able-audit.b-cdn.net/';

/** AI test rule ids use the underscore form, e.g. "EA_AI_R12". */
const AI_RULE_PREFIX = 'EA_AI_R';
/** Defensive: some sources hand us the hyphen form, e.g. "EA-AI-R12". */
const AI_RULE_PREFIX_HYPHEN = 'EA-AI-R';

export type AiRuleCategory =
  | 'images'
  | 'lists'
  | 'tables'
  | 'headings'
  | 'links';

/**
 * True for AI-test rules (`EA_AI_R*`). Accepts the hyphen form defensively.
 * Non-string ids (e.g. the NUMERIC ruleId the ignore store / hidden-rules
 * settings carry for analytical rows) are never AI rules — guard the type so
 * `.startsWith` can't throw on a number.
 */
export function isAiRule(ruleId: string | number | null | undefined): boolean {
  if (typeof ruleId !== 'string') return false;
  return (
    ruleId.startsWith(AI_RULE_PREFIX) ||
    ruleId.startsWith(AI_RULE_PREFIX_HYPHEN)
  );
}

/**
 * Extracts the trailing rule number from any eye-able rule id, regardless of
 * prefix: `EA-R12`, `EA_AI_R12` and `EA-AI-R12` all yield `12`. Pass-through for
 * a numeric id. Returns null when there's no trailing `R<number>` (or the id is
 * neither string nor number). Use this instead of
 * `parseInt(id.replace('EA-R', ''))` so a new rule prefix doesn't need a new
 * parse site (see PR #945 review).
 */
export function extractRuleNumber(
  ruleId: string | number | null | undefined,
): number | null {
  if (typeof ruleId === 'number') {
    return Number.isFinite(ruleId) ? ruleId : null;
  }
  if (typeof ruleId !== 'string') return null;
  const match = /R(\d+)$/.exec(ruleId);
  return match ? Number(match[1]) : null;
}

/** Extracts the numeric part of an AI rule id ("EA_AI_R12" -> 12), or null. */
export function getAiRuleNumber(
  ruleId: string | null | undefined,
): number | null {
  return isAiRule(ruleId) ? extractRuleNumber(ruleId) : null;
}

/**
 * Maps an AI rule to one of the four test categories (+ links). Prefers the
 * `aiCategory` field the producer sets in `ai_rules_meta.json`; falls back to
 * deriving it from the rule-number range when the meta field is absent (older
 * meta / a rule the catalogue doesn't carry yet):
 *   R1–11 + R33 = images, R12–16 = lists, R17–23 + R29 = tables,
 *   R24–28 = headings, R30–32 = links.
 */
export function getAiRuleCategory(
  ruleId: string | null | undefined,
  eaRule?: { aiCategory?: AiRuleCategory | null } | null,
): AiRuleCategory | null {
  if (eaRule?.aiCategory) return eaRule.aiCategory;
  const n = getAiRuleNumber(ruleId);
  if (n === null) return null;
  if (n === 33 || (n >= 1 && n <= 11)) return 'images';
  if (n >= 12 && n <= 16) return 'lists';
  if (n === 29 || (n >= 17 && n <= 23)) return 'tables';
  if (n >= 24 && n <= 28) return 'headings';
  if (n >= 30 && n <= 32) return 'links';
  return null;
}

/** True if the AI rule is an image rule (gets an original-image preview + alt-text suggestion). */
export function isAiImageRule(
  ruleId: string | null | undefined,
  eaRule?: { aiCategory?: AiRuleCategory | null } | null,
): boolean {
  return getAiRuleCategory(ruleId, eaRule) === 'images';
}

/** Directory the AI test publishes per-finding assets under, on the CDN and locally. */
const AI_ASSETS_DIR = 'ally-ai-testing-assets/';

/**
 * Resolves an AI asset path (context/original screenshot) to a full URL.
 * - Absolute http(s) URLs are returned as-is.
 * - Otherwise the path is reduced to its CDN-relative `ally-ai-testing-assets/…`
 *   segment and joined to the AI assets base for the given audit `cdnDirectory`.
 *   This tolerates reports that store an absolute/local path (e.g.
 *   `P:\…\ally-ai-testing-assets\image\…jpeg`) as well as already-relative paths.
 * Returns undefined when the path is missing so the caller can show a placeholder.
 */
export function aiAssetUrl(
  path: string | null | undefined,
  cdnDirectory: string | null | undefined,
): string | undefined {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  if (!cdnDirectory) return undefined;
  const normalized = path.replace(/\\/g, '/');
  const markerIndex = normalized.lastIndexOf(AI_ASSETS_DIR);
  const rel = (
    markerIndex >= 0 ? normalized.slice(markerIndex) : normalized
  ).replace(/^\/+/, '');
  const dir = cdnDirectory.replace(/^\/+|\/+$/g, '');
  return `${AI_ASSETS_CDN_BASE}${dir}/${rel}`;
}
